import React, { useState, useEffect, useCallback, memo } from "react";
import { Row, Col, Card, Statistic, List, Table, Tag, Spin, Alert, Button } from "antd";
import ReactApexChart from "react-apexcharts";
import moment from "moment";
import { getLatestFiveAudits, getAuditCount, getAuditGraphData } from "./cleaningAuditApi";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// audit_status: 0 = audit completed, 1 = rectification completed, 2 = rectification notified
// (matches MajesticERP-CleaningAuditWeb's dashboard/view-content.js)
const STATUS_META = {
  0: { label: "Audit completed", color: "green" },
  1: { label: "Rectification completed", color: "green" },
  2: { label: "Rectification notified", color: "orange" },
};

const Dashboard = () => {
  const [latestAudits, setLatestAudits] = useState([]);
  const [counts, setCounts] = useState(null);
  const [graphData, setGraphData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [auditsRes, countsRes, graphRes] = await Promise.all([
      getLatestFiveAudits(),
      getAuditCount(),
      getAuditGraphData(),
    ]);
    setLatestAudits(!auditsRes.error && Array.isArray(auditsRes.result?.result) ? auditsRes.result.result : []);
    setCounts(!countsRes.error && Array.isArray(countsRes.result?.result) ? countsRes.result.result[0] : null);
    setGraphData(!graphRes.error && Array.isArray(graphRes.result?.result) ? graphRes.result.result : []);
    setLoadError(auditsRes.error && countsRes.error && graphRes.error);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const chartOptions = {
    chart: { type: "area", toolbar: { show: false } },
    xaxis: { categories: graphData.map((g) => MONTH_NAMES[(g.month || 1) - 1]) },
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: 2 },
    colors: ["#2F6FE0", "#1A8F5C"],
    legend: { position: "top" },
  };
  const chartSeries = [
    { name: "Audit Completed", data: graphData.map((g) => g["audit completed"] || 0) },
    { name: "Rectification Completed", data: graphData.map((g) => g["rectification completed"] || 0) },
  ];

  const auditColumns = [
    { title: "Auditor", dataIndex: "auditor_name", key: "auditor_name" },
    {
      title: "Date",
      dataIndex: "audit_date",
      key: "audit_date",
      render: (v) => (v ? moment(v).format("DD-MM-YYYY") : "-"),
    },
    { title: "Score", dataIndex: "audit_score", key: "audit_score" },
    {
      title: "Status",
      dataIndex: "audit_status",
      key: "audit_status",
      render: (status) => {
        const meta = STATUS_META[status] || { label: "Unknown", color: "default" };
        return <Tag color={meta.color}>{meta.label}</Tag>;
      },
    },
  ];

  if (loadError) {
    // antd 4.10.0 (this app's version) doesn't support Alert's "action" prop
    // (added in a later release) — the retry button is a sibling instead.
    return (
      <div>
        <Alert
          type="error"
          showIcon
          message="Couldn't load Cleaning Audit data"
          description="The Cleaning Audit service didn't respond. Try again, or check back shortly."
        />
        <Button size="small" style={{ marginTop: 12 }} onClick={load}>Retry</Button>
      </div>
    );
  }

  return (
    <Spin spinning={loading}>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Card><Statistic title="Completed Audit" value={counts?.audit_complited ?? 0} /></Card>
        </Col>
        <Col span={8}>
          <Card><Statistic title="Completed Rectification" value={counts?.rectification_complited ?? 0} /></Card>
        </Col>
        <Col span={8}>
          <Card><Statistic title="Rectification Notified" value={counts?.rectification_notify ?? 0} /></Card>
        </Col>
      </Row>

      <Card title="Audit Statistics" style={{ marginBottom: 16 }}>
        <ReactApexChart options={chartOptions} series={chartSeries} type="area" height={280} />
      </Card>

      <Row gutter={16}>
        <Col span={10}>
          <Card title="Recent Activities">
            <List
              dataSource={latestAudits}
              locale={{ emptyText: "No recent activity." }}
              renderItem={(item) => {
                const meta = STATUS_META[item.audit_status] || { label: "activity" };
                return (
                  <List.Item>
                    <List.Item.Meta
                      title={`${item.auditor_name} ${meta.label.toLowerCase()}`}
                      description={item.audit_date ? moment(item.audit_date).format("DD-MM-YYYY") : ""}
                    />
                  </List.Item>
                );
              }}
            />
          </Card>
        </Col>
        <Col span={14}>
          <Card title="Latest Audits">
            <Table
              rowKey={(_, i) => i}
              columns={auditColumns}
              dataSource={latestAudits}
              pagination={false}
              size="small"
              locale={{ emptyText: "No audits yet." }}
            />
          </Card>
        </Col>
      </Row>
    </Spin>
  );
};

export default memo(Dashboard);
